import { useId } from '@fluentui/react-hooks';
import AppContext from 'App/AppContext';
import { IPDefaultButton, IPIconButton, IPLink, IPPrimaryButton, IPText } from 'baseComponents';
import { IPCallout } from 'baseComponents/Callout/IPCallout';
import { IPChoiceGroup } from 'baseComponents/ChoiceGroup/IPChoiceGroup';
import { IPDirectionalHint } from 'baseComponents/Dialog/IPDialog';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { globalStackTokensGapExtraSmall, globalStackTokensGapSmall } from 'globalStyles';
import {
  AIFeedbackPreviewFeatureToggleCode,
  isPreviewFeatureToggleEnabled,
} from 'models/copilot/copilotPreviewFeatureToggles';
import { EntityTypes } from 'models/entity';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

//
// The reader's verdict on one written answer. Two icons at the end of the answer, nothing else:
// the cheap signal first, and only the negative one asks a second question. A verdict is never
// required, never blocks the surface it sits on, and is always reversible; the same answer is never
// asked twice, while a newly written one starts over.
//
// The same row serves every AI surface: the insight card, a Copilot panel answer and a room
// summary. Nothing leaves the browser here. The host is handed the verdict and decides what to do
// with it; today that is the preview's console and nothing else.
//

/** What the reader said about the answer. */
export type AIFeedbackVerdict = 'up' | 'down';

/** Why the answer missed, as a code. The wording lives in the locale files, one key each. */
export type AIFeedbackReason = 'incorrect' | 'incomplete' | 'source' | 'other';

/** One verdict as the host receives it, with the answer it was given about. */
export interface IAIFeedback {
  verdict: AIFeedbackVerdict;

  reason?: string;

  note?: string;

  /** Which answer was judged, in the wording of the surface that showed it. */
  answerKey: string;
}

/**
 * Which AI surface asked, and what the verdict hangs from. `insight` anchors on the entity the
 * advice was written about; `chat` anchors on the Copilot session and the panel context the answer
 * was given in. Everything but `source` is optional: a surface that cannot name its anchor still
 * gets a verdict, it just carries less with it.
 */
export interface IAIFeedbackSubject {
  source: 'insight' | 'chat';

  entityType?: EntityTypes;

  entityId?: number;

  sessionId?: number;

  contextKey?: string;
}

/** The reason that is pre-selected when the negative path opens. */
const defaultReason: AIFeedbackReason = 'incorrect';

/**
 * The one gate and the one sender. Without the preview toggle, or without a subject to hang the
 * verdict from, this is `undefined` and the host leaves the thumbs out entirely - every call site
 * reads an absent sender as "do not ask".
 *
 * Nothing is sent anywhere yet: the verdict goes to the console. The subject is carried all the
 * same, so the day this posts to the API no call site has to change.
 */
export const useAIFeedback = (subject?: IAIFeedbackSubject): ((feedback: IAIFeedback) => void) | undefined => {
  const appContext = useContext(AppContext);
  const isEnabled: boolean = isPreviewFeatureToggleEnabled(
    appContext.globalDataCache.settings,
    AIFeedbackPreviewFeatureToggleCode,
  );

  if (!isEnabled || subject === undefined) return undefined;

  return (feedback: IAIFeedback) => {
    console.debug('AI feedback', { ...subject, ...feedback });
  };
};

interface IAIFeedbackProps {
  /**
   * Which answer is being judged. A different value is a different answer: the icons come back and
   * the reader is asked once more. The same value never asks again.
   */
  answerKey: string;

  /** The verdict, for the host to keep. Absent leaves the icons out of the surface entirely. */
  onFeedback?: (feedback: IAIFeedback) => void;
}

/**
 * The feedback row: thumbs, the reason callout behind the thumb down, and the one line that
 * replaces them both once a verdict is in.
 */
const AIFeedback = (props: IAIFeedbackProps) => {
  const { t } = useTranslation(['translation']);
  const dislikeButtonId: string = useId('ai-feedback-dislike');
  const [answerKey, setAnswerKey] = useState<string>(props.answerKey);
  const [isSent, setIsSent] = useState<boolean>(false);
  const [isReasonOpen, setIsReasonOpen] = useState<boolean>(false);
  const [reason, setReason] = useState<AIFeedbackReason>(defaultReason);
  const [note, setNote] = useState<string>('');

  // A new answer is a new question. Everything remembered here is about the answer that was on
  // screen when the verdict was given, so it is dropped the moment that answer is replaced.
  if (answerKey !== props.answerKey) {
    setAnswerKey(props.answerKey);
    setIsSent(false);
    setIsReasonOpen(false);
    setReason(defaultReason);
    setNote('');
  }

  const send = (feedback: Omit<IAIFeedback, 'answerKey'>) => {
    props.onFeedback?.({ ...feedback, answerKey: props.answerKey });
    setIsSent(true);
  };

  const sendReason = () => {
    const written = note.trim();
    setIsReasonOpen(false);
    send({ verdict: 'down', reason: reason, note: written.length === 0 ? undefined : written });
  };

  /** Escape and Cancel leave the same way: the callout closes and no verdict is recorded. */
  const closeReason = () => {
    setIsReasonOpen(false);
  };

  const undo = () => {
    setIsSent(false);
    setReason(defaultReason);
    setNote('');
  };

  if (isSent) {
    return (
      <IPStack horizontal wrap verticalAlign="center" tokens={globalStackTokensGapExtraSmall}>
        <IPText variant="small">{t('translation:General.AiInsight.FeedbackThanks')}</IPText>
        <IPLink onClick={undo}>
          <IPText variant="small">{t('translation:General.AiInsight.FeedbackUndo')}</IPText>
        </IPLink>
      </IPStack>
    );
  }

  return (
    <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapExtraSmall}>
      <IPIconButton
        iconProps={{ iconName: 'Like' }}
        ariaLabel={t('translation:General.AiInsight.FeedbackUp')}
        title={t('translation:General.AiInsight.FeedbackUp')}
        onClick={() => send({ verdict: 'up' })}
      />
      <IPIconButton
        id={dislikeButtonId}
        iconProps={{ iconName: 'Dislike' }}
        ariaLabel={t('translation:General.AiInsight.FeedbackDown')}
        title={t('translation:General.AiInsight.FeedbackDown')}
        onClick={() => setIsReasonOpen(true)}
      />
      {isReasonOpen && (
        <IPCallout
          target={`#${dislikeButtonId}`}
          directionalHint={IPDirectionalHint.bottomRightEdge}
          role="dialog"
          ariaLabel={t('translation:General.AiInsight.FeedbackReasonTitle')}
          setInitialFocus
          onDismiss={closeReason}
          styles={{ root: { padding: '10px 12px', width: 320, maxWidth: '90vw' } }}
        >
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPChoiceGroup
              label={t('translation:General.AiInsight.FeedbackReasonTitle')}
              selectedKey={reason}
              options={[
                { key: 'incorrect', text: t('translation:General.AiInsight.FeedbackReasonIncorrect') },
                { key: 'incomplete', text: t('translation:General.AiInsight.FeedbackReasonIncomplete') },
                { key: 'source', text: t('translation:General.AiInsight.FeedbackReasonSource') },
                { key: 'other', text: t('translation:General.AiInsight.FeedbackReasonOther') },
              ]}
              onChange={(_ev, option) => {
                if (option === undefined) return;
                setReason(option.key as AIFeedbackReason);
              }}
            />
            <IPTextField
              multiline
              rows={3}
              resizable={false}
              value={note}
              placeholder={t('translation:General.AiInsight.FeedbackNotePlaceholder')}
              ariaLabel={t('translation:General.AiInsight.FeedbackNotePlaceholder')}
              onChange={(_ev, newValue) => setNote(newValue ?? '')}
            />
            <IPStack horizontal horizontalAlign="end" tokens={globalStackTokensGapSmall}>
              <IPPrimaryButton text={t('translation:General.AiInsight.FeedbackSend')} onClick={sendReason} />
              <IPDefaultButton text={t('translation:General.AiInsight.FeedbackCancel')} onClick={closeReason} />
            </IPStack>
          </IPStack>
        </IPCallout>
      )}
    </IPStack>
  );
};

export default AIFeedback;
